import IconSync from "assets/img/icon-sync.svg?react"
import IconUpload from "assets/img/icon-upload.svg?react"
import IconThreeDots from "assets/img/icon-three-dots.svg?react"

import { FC, useState } from "react"

import { InfoDropdown } from "components/InfoDropdown"
import Button from "components/Button/Button"
import PartialNoteCreateModal, { PartialNoteType } from "components/PartialNoteCreateModal/PartialNoteCreateModal"
import EditOrderNumberModal from "components/EditOrderNumberModal/EditOrderNumberModal"
import { Grid } from "components/Grid"
import { Guid, IApiLink } from "@/types"
import { OutgoingInvoice, OutgoingInvoiceType } from "models/OutgoingInvoice"
import OutgoingInvoiceControlsDropdownMenu from "./OutgoingInvoiceControlsDropdownMenu"

interface IOutgoingInvoiceControlsProps {
    reservationId: Guid
    invoiceId: Guid
    isSendToErpPending?: boolean
    isSyncPending?: boolean
    isAdHoc?: boolean
    resubmitInvoiceLink?: IApiLink
    isInvoicePrinted?: boolean
    onNoteCreated?(): Promise<void>
    onSendToErpClick?(): Promise<void>
    onErpInvoiceSyncClick?(): Promise<void>
    onInvoiceFetched?(outgoingInvoice: OutgoingInvoice): void
    onCustomerBlocked(): void
    onInvoiceBlocked(): void
    onInvoiceResubmitted?(invoiceId: Guid): void
    onInvoiceChanged?(): void | Promise<void>
    disabled?: boolean
}

const OutgoingInvoiceControls: FC<IOutgoingInvoiceControlsProps> = ({
    reservationId,
    invoiceId,
    isSendToErpPending,
    isSyncPending,
    isAdHoc,
    resubmitInvoiceLink,
    isInvoicePrinted,
    onNoteCreated,
    onSendToErpClick,
    onErpInvoiceSyncClick,
    onInvoiceFetched,
    onCustomerBlocked,
    onInvoiceBlocked,
    onInvoiceResubmitted,
    onInvoiceChanged,
    disabled,
}) => {
    const [creatingPartialNoteType, setCreatingPartialNoteType] = useState<PartialNoteType | null>(null)
    const [editOrderNumberTarget, setEditOrderNumberTarget] = useState<{ link: IApiLink, currentOrderNumber?: string } | null>(null)
    const shouldShowSendToErpButton = !!onSendToErpClick
    const shouldShowSyncButton = !!onErpInvoiceSyncClick

    return (
        <>
            <Grid columns={`${shouldShowSendToErpButton ? "max-content " : ""}${shouldShowSyncButton ? "max-content " : ""} max-content`} gap="1.6rem">
                {!!shouldShowSendToErpButton &&
                    <Button
                        theme="grey"
                        round
                        icon={<IconUpload />}
                        onClick={onSendToErpClick}
                        disabled={isSendToErpPending || isSyncPending || disabled}
                        loading={isSendToErpPending}
                    />
                }
                {!!shouldShowSyncButton &&
                    <Button
                        theme="grey"
                        round
                        icon={<IconSync />}
                        onClick={onErpInvoiceSyncClick}
                        disabled={isSendToErpPending || isSyncPending || disabled}
                        loading={isSyncPending}
                    />
                }
                <InfoDropdown
                    label={() => (
                        <Button
                            theme="grey"
                            round
                            icon={<IconThreeDots />}
                            disabled={disabled}
                        />
                    )}
                    placement="bottom-end"
                    content={closeInfoDropdown => (
                        <OutgoingInvoiceControlsDropdownMenu
                            reservationId={reservationId}
                            invoiceId={invoiceId}
                            hidePartialNotes={isAdHoc}
                            resubmitInvoiceLink={resubmitInvoiceLink}
                            isInvoicePrinted={isInvoicePrinted}
                            close={closeInfoDropdown}
                            onPartialNoteCreateClick={setCreatingPartialNoteType}
                            onInvoiceFetched={onInvoiceFetched}
                            onInvoiceResubmitted={onInvoiceResubmitted}
                            onEditOrderNumberClick={(link, currentOrderNumber) => setEditOrderNumberTarget({ link, currentOrderNumber })}
                            onInvoiceChanged={onInvoiceChanged}
                        />
                    )}
                />
            </Grid>
            {!!creatingPartialNoteType &&
                <PartialNoteCreateModal
                    type={creatingPartialNoteType}
                    invoiceId={invoiceId}
                    onCreated={onNoteCreated}
                    onClose={() => setCreatingPartialNoteType(null)}
                    onCustomerBlocked={onCustomerBlocked}
                    onInvoiceBlocked={onInvoiceBlocked}
                />
            }
            {!!editOrderNumberTarget &&
                <EditOrderNumberModal
                    link={editOrderNumberTarget.link}
                    currentOrderNumber={editOrderNumberTarget.currentOrderNumber}
                    onSaved={async () => { await onInvoiceChanged?.() }}
                    onClose={() => setEditOrderNumberTarget(null)}
                />
            }
        </>
    )
}

export default OutgoingInvoiceControls
